import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Minus, Plus, ShoppingCart, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { productImage } from "@/lib/images";

const TIER_LABELS = { day: "1 DAY", week: "1 WEEK", "15day": "15 DAYS", month: "1 MONTH", "3month": "3 MONTHS", lifetime: "LIFETIME", instant: "INSTANT" };

export default function CartDrawer() {
  const { items, remove, setQty, clear, subtotal, open, setOpen } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [tos, setTos] = useState(false);
  const [method, setMethod] = useState("crypto");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && user?.email && !email) setEmail(user.email);
  }, [open, user, email]);

  if (!open) return null;

  const discount = applied ? subtotal * (applied.discount_pct / 100) : 0;
  const total = Math.max(0, subtotal - discount);

  const applyCode = async () => {
    if (!code.trim()) return;
    try {
      const r = await api.get("/codes/validate", { params: { code: code.trim() } });
      setApplied(r.data);
      toast.success(`Code ${r.data.code} applied — ${r.data.discount_pct}% off`);
    } catch {
      setApplied(null);
      toast.error("Invalid or inactive code");
    }
  };

  const placeOrder = async () => {
    if (!user) { toast.error("Log in before checkout"); nav("/login"); return; }
    if (!user.discord_connected) { toast.error("Connect Discord before checkout"); nav("/dashboard?discord=required"); return; }
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email.trim())) { toast.error("Enter your email — keys deliver there"); return; }
    if (!tos) { toast.error("Accept the Terms of Service first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((x) => ({ slug: x.slug, tier: x.tier, quantity: x.quantity })),
        email: email.trim(),
        method,
        origin_url: window.location.origin,
        code: applied ? applied.code : undefined,
      });
      clear();
      setOpen(false);
      if (method === "crypto" && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        nav(`/pay/${data.session_id}`);
      }
    } catch (e) {
      const status = e.response?.status;
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
      if (status === 401) nav("/login");
      if (status === 403) nav("/dashboard?discord=required");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[140]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="absolute right-0 top-0 h-full w-[min(440px,100vw)] bg-black border-l border-white/15 flex flex-col animate-[cartIn_.25s_ease-out]">
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="font-mono text-xs tracking-[0.25em] text-white/60 flex items-center gap-2">
            <ShoppingCart size={14} /> CART ({items.reduce((n, x) => n + x.quantity, 0)})
          </div>
          <button onClick={() => setOpen(false)} data-testid="cart-close" className="text-white/50 hover:text-white transition-colors" aria-label="Close cart">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {items.length === 0 && (
            <div className="py-16 text-center font-mono text-xs tracking-[0.2em] text-white/40" data-testid="cart-empty">// CART EMPTY — GRAB SOMETHING</div>
          )}
          {items.map((x) => (
            <div key={`${x.slug}-${x.tier}`} className="flex gap-4 rounded-2xl border border-white/10 p-4" data-testid={`cart-item-${x.slug}-${x.tier}`}>
              <div className="w-16 h-16 rounded-xl border border-white/10 bg-black shrink-0 overflow-hidden">
                <img src={productImage(x.image)} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(100%) brightness(60%) contrast(115%)" }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-display uppercase truncate">{x.name}</div>
                <div className="font-mono text-[10px] tracking-[0.15em] text-white/40 mt-0.5">{TIER_LABELS[x.tier] || x.tier}</div>
                <div className="mt-2 flex items-center gap-3">
                  <button onClick={() => setQty(x.slug, x.tier, x.quantity - 1)} data-testid={`cart-minus-${x.slug}-${x.tier}`} className="rounded-full border border-white/15 w-6 h-6 flex items-center justify-center text-white/60 hover:text-white hover:border-white transition-colors"><Minus size={11} /></button>
                  <span className="font-mono text-sm w-4 text-center">{x.quantity}</span>
                  <button onClick={() => setQty(x.slug, x.tier, x.quantity + 1)} data-testid={`cart-plus-${x.slug}-${x.tier}`} className="rounded-full border border-white/15 w-6 h-6 flex items-center justify-center text-white/60 hover:text-white hover:border-white transition-colors"><Plus size={11} /></button>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display text-lg">${(x.price * x.quantity).toFixed(2)}</div>
                <button onClick={() => remove(x.slug, x.tier)} data-testid={`cart-remove-${x.slug}-${x.tier}`} className="mt-2 text-white/35 hover:text-red-400 transition-colors" title="Remove">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/10 px-6 py-5 space-y-4">
            <div className="flex items-center justify-between font-mono text-xs tracking-[0.15em]">
              <span className="text-white/50">SUBTOTAL</span>
              <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            {applied && (
              <div className="flex items-center justify-between font-mono text-xs tracking-[0.15em]">
                <span className="text-white/50">CODE {applied.code}</span>
                <span data-testid="cart-discount">-${discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-[0.2em] text-white/40">TOTAL</span>
              <span className="font-display text-3xl" data-testid="cart-total">${total.toFixed(2)}</span>
            </div>
            <div className="font-mono text-[9px] tracking-[0.15em] text-white/35">// RETURNING-BUYER + RANK DISCOUNTS AUTO-APPLY AT PAYMENT</div>

            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="EMAIL — KEYS DELIVER HERE"
              data-testid="cart-email"
              className="w-full bg-transparent border border-white/15 px-4 py-3 font-mono text-xs focus:border-white focus:outline-none placeholder:text-white/25" />
            <div className="flex gap-2">
              <input value={code} onChange={(e) => { setCode(e.target.value); setApplied(null); }} placeholder="CREATOR CODE (OPTIONAL)"
                data-testid="cart-code"
                className="flex-1 bg-transparent border border-white/15 px-4 py-3 font-mono text-xs uppercase focus:border-white focus:outline-none placeholder:text-white/25" />
              <button onClick={applyCode} data-testid="cart-apply-code" className="btn-ghost px-4 py-3 text-[10px]">APPLY</button>
            </div>

            <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="cart-tos">
              <input type="checkbox" checked={tos} onChange={(e) => setTos(e.target.checked)} className="mt-0.5 accent-white" />
              <span className="font-mono text-[10px] tracking-[0.08em] text-white/50 leading-relaxed">
                I ACCEPT THE TERMS OF SERVICE — DIGITAL GOODS, ALL SALES FINAL
              </span>
            </label>

            <div className="grid grid-cols-2 gap-px rounded-xl overflow-hidden bg-white/10 border border-white/10">
              {[["crypto", "CRYPTO"], ["manual", "VENMO / APPLE PAY"]].map(([m, l]) => (
                <button key={m} onClick={() => setMethod(m)} data-testid={`cart-method-${m}`}
                  className={`py-3 font-mono text-[10px] tracking-[0.15em] transition-colors ${method === m ? "bg-white text-black" : "bg-black text-white/50 hover:text-white"}`}>
                  {l}
                </button>
              ))}
            </div>

            <button onClick={placeOrder} disabled={busy} data-testid="cart-place-order"
              className="btn-inj w-full py-4 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : null}
              PLACE ORDER — ${total.toFixed(2)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
